import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BookOpen, MessageSquareQuote, Calendar, Sparkles, ChevronRight } from "lucide-react";

const BibleStudyShowcase = () => {
  const { t } = useTranslation();

  return (
    <section className="py-16 md:py-24 relative overflow-hidden">
      {/* Background with blue gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/95 via-indigo-900/90 to-slate-900/95" />
      
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-sky-400/5 rounded-full blur-2xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-300 px-4 py-1.5 rounded-full text-sm font-bold mb-4 border border-blue-400/30">
            <Sparkles size={16} className="text-blue-400" />
            {t("bibleStudy.newLabel", "NEW FEATURE")}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
            {t("bibleStudy.title", "Bible Catena")}
          </h2>
          <p className="text-xl text-blue-200 mb-2">
            {t("bibleStudy.subtitle", "Scripture Learning for Catholic Families")}
          </p>
          <p className="text-blue-300/80 max-w-2xl mx-auto">
            {t("bibleStudy.tagline", "Deep dive into Scripture with Catholic commentary, meditation prompts, and liturgical connections.")}
          </p>
        </div>

        {/* Two-column layout */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-10">
          {/* Featured Book Spotlight */}
          <div className="bg-gradient-to-br from-blue-800/50 to-indigo-800/50 rounded-2xl p-6 border border-blue-500/20 backdrop-blur-sm">
            <span className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-3 block">
              {t("bibleStudy.featuredBook", "Featured Book")}
            </span>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-lg">
                <BookOpen size={32} className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-1">
                  {t("bibleStudy.matthew.title", "The Book of Matthew")}
                </h3>
                <p className="text-blue-300 text-sm">
                  {t("bibleStudy.matthew.subtitle", "The Gospel of the Kingdom")}
                </p>
              </div>
            </div>
            <p className="text-blue-200/80 text-sm mb-5 leading-relaxed">
              {t("bibleStudy.matthew.desc", "Begin your journey through Matthew's Gospel with rich Catholic commentary, spiritual reflections, and connections to the liturgical year.")}
            </p>
            <Link
              to="/bible-study/matthew"
              className="inline-flex items-center gap-2 bg-white text-blue-900 px-5 py-2.5 rounded-lg font-semibold hover:bg-blue-50 transition-colors shadow-lg"
            >
              {t("bibleStudy.startReading", "Start Reading")}
              <ChevronRight size={18} />
            </Link>
          </div>

          {/* Stats and Features */}
          <div className="flex flex-col gap-6">
            {/* Stats */}
            <div className="bg-blue-800/30 rounded-xl p-5 border border-blue-500/20 backdrop-blur-sm">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-3xl font-bold text-white">28</div>
                  <div className="text-blue-300 text-sm">{t("bibleStudy.stats.chapters", "Chapters")}</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">3</div>
                  <div className="text-blue-300 text-sm">{t("bibleStudy.stats.languages", "Languages")}</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">129</div>
                  <div className="text-blue-300 text-sm">{t("bibleStudy.stats.sections", "Sections")}</div>
                </div>
              </div>
            </div>

            {/* Features grid */}
            <div className="grid grid-cols-1 gap-3">
              <div className="flex items-center gap-3 bg-blue-800/20 rounded-lg p-3 border border-blue-500/10">
                <div className="w-10 h-10 rounded-lg bg-blue-600/30 flex items-center justify-center">
                  <BookOpen size={20} className="text-blue-300" />
                </div>
                <span className="text-white font-medium">{t("bibleStudy.features.commentary", "Catholic Commentary")}</span>
              </div>
              <div className="flex items-center gap-3 bg-blue-800/20 rounded-lg p-3 border border-blue-500/10">
                <div className="w-10 h-10 rounded-lg bg-indigo-600/30 flex items-center justify-center">
                  <MessageSquareQuote size={20} className="text-indigo-300" />
                </div>
                <span className="text-white font-medium">{t("bibleStudy.features.meditation", "Meditation Prompts")}</span>
              </div>
              <div className="flex items-center gap-3 bg-blue-800/20 rounded-lg p-3 border border-blue-500/10">
                <div className="w-10 h-10 rounded-lg bg-sky-600/30 flex items-center justify-center">
                  <Calendar size={20} className="text-sky-300" />
                </div>
                <span className="text-white font-medium">{t("bibleStudy.features.liturgical", "Liturgical Connections")}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main CTA */}
        <div className="text-center">
          <Link
            to="/bible-study"
            className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-4 rounded-xl text-lg font-bold hover:from-blue-600 hover:to-indigo-700 transition-all shadow-xl hover:shadow-2xl hover:scale-105 transform"
          >
            <BookOpen size={24} />
            {t("bibleStudy.enterStudy", "Start Bible Catena")}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BibleStudyShowcase;
